import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { colorStyling } from "helpers/color";
import { TypographyNormal } from "components/customComponent";
import { initiateAXIOS } from "config/axios";
import { announcementAPILimit } from "api";
import { useDispatch, useSelector } from "react-redux";
import {
  SET_ERROR_MESSAGE_ANNOUNCEMENT_DIALOG,
  SET_ERROR_PROMPT_ANNOUNCEMENT_DIALOG,
} from "store/actionTypes/announcementActionType";
import { useDebounce } from "components/hooks";
import { parseVariables } from "helpers/localize";
import parser from "html-react-parser";
import { AnnouncementPurpleIcon } from "icons";
import { isEmpty } from "lodash";
import { multiActivateAnnouncement } from "store/actions/announcementAction";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { Link } from "react-router-dom";

const ActivateMultipleAnnouncement = () => {
  const dispatch = useDispatch();
  const { t: tCommon } = useTranslation("common");
  const { t } = useTranslation("announcement");

  const {
    isErrorPromptAnnouncementDialog: isError,
    errorMessageAnnouncementDialog: errorMessage,
  } = useSelector((state) => state.announcement);

  const [open, setOpen] = useState(false);
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [openFailedDialog, setOpenFailedDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("idDesc");

  const [selectedAnnouncement, setSelectedAnnouncement] = useState([]);
  const [listAnnouncement, setListAnnouncement] = useState([]);
  const [failedItems, setFailedItems] = useState([]);

  const debouncedSearchTerm = useDebounce(search, 1000);

  useEffect(() => {
    if (open) {
      getAnnouncement(debouncedSearchTerm);
    }

    //eslint-disable-next-line
  }, [open, debouncedSearchTerm]);

  const getAnnouncement = async (name) => {
    const access_token = sessionStorage.getItem("access_token");
    setLoading(true);

    try {
      const { data } = await initiateAXIOS.get(
        announcementAPILimit +
          `&sortBy=${sortBy}&isApproved=true&isActive=false` +
          (isEmpty(name) ? "" : `&nameLike=${name}`),
        {
          headers: { authorization: `Bearer ${access_token}` },
        }
      );

      setListAnnouncement(data.items);
    } catch (e) {
      const errorMessage = e.response.data.message;
      dispatch({ type: SET_ERROR_PROMPT_ANNOUNCEMENT_DIALOG, payload: true });
      dispatch({
        type: SET_ERROR_MESSAGE_ANNOUNCEMENT_DIALOG,
        payload: errorMessage,
      });
    } finally {
      setLoading(false);
    }
  };

  const clearState = () => {
    setSelectedAnnouncement([]);
    setListAnnouncement([]);
    setFailedItems([]);
    setSearch("");
    setSortBy("idDesc");
  };

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClickClose = () => {
    setOpen(false);
    clearState();
  };

  const handleSortByChange = (e) => {
    setSortBy(e.target.value);
  };

  const handleCloseAlert = () => {
    dispatch({ type: SET_ERROR_PROMPT_ANNOUNCEMENT_DIALOG, payload: false });
  };

  const handleSelectAll = () => {
    setSelectedAnnouncement((prev) =>
      Array.from(
        new Map(
          [...prev, ...listAnnouncement].map((item) => [item.id, item])
        ).values()
      )
    );
  };

  const handleChangeCheckbox = (event) => {
    if (event.target.checked) {
      setSelectedAnnouncement([
        ...selectedAnnouncement,
        listAnnouncement.filter(
          (item) => +item.id === +event.target.value
        )?.[0],
      ]);
    } else {
      const filtered = selectedAnnouncement.filter(
        (item) => +item.id !== +event.target.value
      );
      setSelectedAnnouncement(filtered);
    }
  };

  const handleOpenConfirmation = () => {
    setOpenConfirmation(true);
  };

  const handleCloseConfirmation = () => {
    setOpenConfirmation(false);
  };

  const handleSubmit = () => {
    dispatch(multiActivateAnnouncement(selectedAnnouncement, handleCallback));
  };

  const handleCallback = (status, data) => {
    handleCloseConfirmation();
    switch (status) {
      case "success":
        handleClickClose();
        break;
      case "error":
        setOpenFailedDialog(true);
        setFailedItems(data);
        break;

      default:
        break;
    }
  };

  const handleCloseFailedDialog = () => {
    setOpenFailedDialog(false);
    handleClickClose();
  };

  return (
    <>
      <Button onClick={handleClickOpen} variant="outlined">
        {t("multiActivation.mainActivateBtn")}
      </Button>

      <Dialog maxWidth="sm" open={open} onClose={handleClickClose} fullWidth>
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            textTransform: "capitalize",
          }}
        >
          {t("multiActivation.mainActivateDialogTitle")}
          <IconButton onClick={handleClickClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <Divider />

        <DialogContent>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Box sx={{ width: "70%", mr: 1 }}>
              <TextField
                fullWidth
                size="small"
                label={t("filter.searchPlaceholder")}
                placeholder={t("filter.searchPlaceholder")}
                value={search}
                disabled={loading}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              ></TextField>
            </Box>

            <Box sx={{ width: "30%" }}>
              <TextField
                fullWidth
                size="small"
                label={tCommon("sortLabel.default")}
                select
                SelectProps={{ native: true }}
                value={sortBy}
                disabled={loading}
                onChange={handleSortByChange}
              >
                <option key="idAsc" value="idAsc">
                  {tCommon("sortLabel.idAsc")}
                </option>
                <option key="idDesc" value="idDesc">
                  {tCommon("sortLabel.idDesc")}
                </option>
                <option key="nameAsc" value="nameAsc">
                  {tCommon("sortLabel.nameAsc")}
                </option>
                <option key="nameDesc" value="nameDesc">
                  {tCommon("sortLabel.nameDesc")}
                </option>
              </TextField>
            </Box>
          </Box>

          {isError && (
            <Alert
              sx={{ mt: 1 }}
              severity="error"
              variant="outlined"
              onClose={handleCloseAlert}
            >
              {errorMessage}
            </Alert>
          )}

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 2,
              py: 1.25,
              borderRadius: "6px",
              backgroundColor: "info.light",
              color: "info.contrastText",
              my: 1,
            }}
          >
            <Typography sx={{ fontWeight: 600 }}>
              {t("multiActivation.selectCountLabel")}
            </Typography>

            <Typography sx={{ fontWeight: 700 }}>
              {selectedAnnouncement?.length ?? 0}
            </Typography>
          </Box>

          <Box
            sx={{
              maxHeight: 300,
              overflowY: "auto",
              px: 2,
              pb: 2,
              borderRadius: "6px",
              border: `1px solid ${colorStyling.white.border}`,
              mt: 1,
            }}
          >
            {/* Header */}
            <Box
              sx={{
                position: "sticky",
                top: 0,
                zIndex: 1,
                backgroundColor: "background.paper",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 2,
                py: 1,
                mb: 1,
                borderBottom: `1px solid ${colorStyling.white.border}`,
              }}
            >
              <TypographyNormal sx={{ fontWeight: 600 }}>
                {t("multiActivation.announcementHeader")}
              </TypographyNormal>

              <Button onClick={handleSelectAll}>
                {t("multiActivation.selectAllBtn")}
              </Button>
            </Box>
            {!loading && !listAnnouncement?.length ? (
              <Alert severity="info" variant="outlined">
                {parser(
                  parseVariables(tCommon("assign.noDataFound"), {
                    type: "announcement",
                  })
                )}
              </Alert>
            ) : null}

            {loading && (
              <Box
                display="flex"
                justifyContent="center"
                flexDirection="column"
                alignItems="center"
                sx={{ py: 1 }}
              >
                <CircularProgress
                  size={20}
                  thickness={3}
                  sx={{ color: colorStyling.primary }}
                />
                <TypographyNormal
                  sx={{
                    color: colorStyling.primary,
                    marginTop: "15px",
                    fontWeight: 300,
                  }}
                >
                  {tCommon("loadingDataList")}
                </TypographyNormal>
              </Box>
            )}

            {listAnnouncement?.map((item) => (
              <Box
                key={item.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 2,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <AnnouncementPurpleIcon />

                  <Box
                    sx={{
                      ml: 2,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <TypographyNormal sx={{ fontWeight: 600, mb: 1 }}>
                      {item.name}
                    </TypographyNormal>
                  </Box>
                </Box>

                <Checkbox
                  value={item.id}
                  onChange={handleChangeCheckbox}
                  checked={
                    !isEmpty(
                      selectedAnnouncement.filter((sel) => +sel.id === +item.id)
                    )
                  }
                />
              </Box>
            ))}
          </Box>
        </DialogContent>

        <DialogActions>
          <Button variant="outlined" fullWidth onClick={handleClickClose}>
            {tCommon("cancelBtn")}
          </Button>

          <Button
            onClick={handleOpenConfirmation}
            variant="contained"
            fullWidth
            disabled={loading || isEmpty(selectedAnnouncement)}
          >
            {loading ? <CircularProgress size={24} /> : tCommon("submitBtn")}
          </Button>

          <ConfirmActivateAnnouncementDialog
            open={openConfirmation}
            onClose={() => handleCloseConfirmation()}
            items={selectedAnnouncement}
            loading={loading}
            onConfirm={handleSubmit}
          />

          <FailedActivationDialog
            open={openFailedDialog}
            failedItems={failedItems}
            onClose={() => handleCloseFailedDialog()}
          />
        </DialogActions>
      </Dialog>
    </>
  );
};

const ConfirmActivateAnnouncementDialog = ({
  open,
  items,
  onClose,
  onConfirm,
  loading,
}) => {
  const { t: tCommon } = useTranslation("common");
  const { t } = useTranslation("announcement");

  return (
    <Dialog
      open={open}
      onClose={loading ? null : onClose}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>
        {t("multiActivation.confirmActivateDialogTitle")}
      </DialogTitle>

      <DialogContent dividers>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {t("multiActivation.confirmActivateText")}
        </Typography>

        {/* Scroll container */}
        <Box
          sx={{
            maxHeight: 240,
            overflowY: "auto",
            border: 1,
            borderColor: "divider",
            borderRadius: 1,
          }}
        >
          <List dense>
            {items.map((item) => (
              <ListItem key={item.id}>
                <Box
                  key={item.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 2,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <AnnouncementPurpleIcon />

                    <Box
                      sx={{
                        ml: 2,
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <TypographyNormal sx={{ fontWeight: 600, mb: 1 }}>
                        {item.name}
                      </TypographyNormal>
                    </Box>
                  </Box>
                </Box>
              </ListItem>
            ))}
          </List>
        </Box>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 1, display: "block" }}
        >
          {items.length + " " + t("multiActivation.confirmSelectCountText")}
        </Typography>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={onClose}
          variant="outlined"
          disabled={loading}
          fullWidth
        >
          {tCommon("cancelBtn")}
        </Button>

        <Button
          fullWidth
          onClick={onConfirm}
          variant="contained"
          color="primary"
          disabled={loading}
        >
          {loading ? (
            <CircularProgress size={24} />
          ) : (
            t("multiActivation.confirmActivateBtn")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const FailedActivationDialog = ({ open, onClose, failedItems }) => {
  const { t: tCommon } = useTranslation("common");
  const { t } = useTranslation("announcement");
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <ErrorOutlineIcon color="error" />
        {t("multiActivation.failedActivateDialogTitle")}
      </DialogTitle>

      <DialogContent dividers>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {t("multiActivation.failedActivateDialogText")}
        </Typography>

        <Box
          sx={{
            maxHeight: 300,
            overflowY: "auto",
          }}
        >
          {failedItems.map((item, index) => (
            <ListItem key={item.id} disableGutters>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  width: "100%",
                  mb: 2,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                  <AnnouncementPurpleIcon />

                  <Box
                    sx={{
                      ml: 2,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    {/* Announcement name (link) */}
                    <TypographyNormal
                      component={Link}
                      to={`/announcement/detail/${item.id}`}
                      sx={{
                        fontWeight: 600,
                        mb: 0.5,
                        color: "primary.main",
                        textDecoration: "none",
                        "&:hover": {
                          textDecoration: "underline",
                        },
                      }}
                    >
                      {item.name}
                    </TypographyNormal>

                    {/* Error message */}
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{
                        maxWidth: 420,
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                      }}
                    >
                      {item.error}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </ListItem>
          ))}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button variant="contained" color="primary" fullWidth onClick={onClose}>
          {tCommon("closeBtn")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ActivateMultipleAnnouncement;
